// ============================================================================
// Device detection utilities
// Single source of truth for device-type classification (telemetry, UI hints).
// ============================================================================

const MOBILE_UA_REGEX = /Mobi|Android|iPhone|iPod/i;
const TABLET_UA_REGEX = /iPad|Tablet|PlayBook|Silk/i;

/**
 * Classify the current client as "desktop" | "tablet" | "mobile".
 * Uses UA Client Hints when available, falls back to UA regex + viewport width
 * (768px mobile breakpoint per design system; tablet = touch device 768-1024).
 */
export const getDeviceType = () => {
  try {
    const ua = navigator.userAgent || "";

    // iPadOS 13+ reports a desktop Mac UA but has touch support
    const isIpadOsDesktopUa =
      /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;

    if (TABLET_UA_REGEX.test(ua) || isIpadOsDesktopUa) return "tablet";

    if (navigator.userAgentData?.mobile || MOBILE_UA_REGEX.test(ua)) {
      return "mobile";
    }

    const hasTouch = navigator.maxTouchPoints > 0 || "ontouchstart" in window;
    const width = window.innerWidth || 0;
    if (hasTouch && width >= 768 && width <= 1024) return "tablet";
    if (hasTouch && width < 768) return "mobile";

    return "desktop";
  } catch (e) {
    return "desktop";
  }
};

/**
 * True when running as an installed PWA (standalone display mode).
 */
export const isStandalonePWA = () => {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
      window.navigator.standalone === true
    );
  } catch (e) {
    return false;
  }
};

// ============================================================================
// PWA install prompt helpers
// ============================================================================

const IN_APP_BROWSER_UA_REGEX =
  /FBAN|FBAV|Instagram|Line\/|Twitter|TikTok|Snapchat|; wv\)|WhatsApp|Telegram/i;

/**
 * True on iPhone / iPad / iPod (including iPadOS desktop-style UA).
 */
export const isIOS = () => {
  try {
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  } catch (e) {
    return false;
  }
};

/**
 * True inside an embedded webview (Instagram, Facebook, WhatsApp, Android WebView…)
 * where "Add to Home Screen" is not available.
 */
export const isInAppBrowser = () => {
  try {
    return IN_APP_BROWSER_UA_REGEX.test(navigator.userAgent || "");
  } catch (e) {
    return false;
  }
};

const PWA_INSTALLED_KEY = "clinikit_pwa_installed";
const PWA_INSTALL_NEVER_PREFIX = "clinikit_pwa_install_never_"; // localStorage — "do not show again"
const PWA_INSTALL_LATER_PREFIX = "clinikit_pwa_install_later_"; // sessionStorage — "show me later"

/**
 * Persist that the app was installed on this browser profile (appinstalled event).
 * Once set, the install banner never shows again in this browser.
 */
export const markPwaInstalled = () => {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, new Date().toISOString());
  } catch (e) {
    /* storage unavailable — ignore */
  }
};

/**
 * "Do not show again" — permanently hide the install banner for this therapist
 * on this browser profile.
 */
export const markPwaInstallPromptNever = (therapistId) => {
  if (!therapistId) return;
  try {
    localStorage.setItem(
      `${PWA_INSTALL_NEVER_PREFIX}${therapistId}`,
      new Date().toISOString()
    );
  } catch (e) {
    /* storage unavailable — ignore */
  }
};

/**
 * "Show me later" — hide the install banner for the rest of this browser tab
 * session. It comes back on the next visit (new tab / app reopened).
 */
export const snoozePwaInstallPrompt = (therapistId) => {
  if (!therapistId) return;
  try {
    sessionStorage.setItem(
      `${PWA_INSTALL_LATER_PREFIX}${therapistId}`,
      new Date().toISOString()
    );
  } catch (e) {
    /* storage unavailable — ignore */
  }
};

/**
 * Whether to offer "install CliniKit" to this user right now:
 * mobile device, regular browser tab (not an installed PWA), not installed
 * before on this browser, not permanently dismissed ("do not show again"),
 * and not snoozed for this tab session ("show me later").
 *
 * Shown on every visit otherwise — there is no display-count / interval cap.
 *
 * Note: this only decides *eligibility*. In-app browsers are eligible too —
 * the banner shows an "open in a real browser" variant for them.
 */
export const shouldPromptForPwaInstall = (therapistId) => {
  if (!therapistId) return false;
  try {
    if (getDeviceType() !== "mobile") return false;
    if (isStandalonePWA()) return false;
    if (localStorage.getItem(PWA_INSTALLED_KEY)) return false;
    if (localStorage.getItem(`${PWA_INSTALL_NEVER_PREFIX}${therapistId}`)) {
      return false;
    }
    if (sessionStorage.getItem(`${PWA_INSTALL_LATER_PREFIX}${therapistId}`)) {
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};
